import {
  BlockStack,
  Banner,
  Button,
  Card,
  InlineStack,
  Text,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  applyReplenishSuggestion,
  listReplenishSuggestions,
  type ReplenishApplyResult,
} from "../api/reorder_policies";
import { t } from "../lib/i18n";

export const REPLENISH_SUGGESTION_QUERY_KEY = ["replenish-suggestions"];

interface Props {
  /** 採用後に発注点一覧を引き直させる（親が持つクエリキー）。 */
  onApplied: () => void;
  disabled?: boolean;
}

function applySummary(result: ReplenishApplyResult): string {
  const parts = [
    t("{n}件に補充日数を設定しました。").replace("{n}", String(result.updated)),
  ];
  if (result.kept_existing > 0) {
    parts.push(
      t("既に入力済みの{n}件はそのままにしました。").replace(
        "{n}",
        String(result.kept_existing),
      ),
    );
  }
  return parts.join(" ");
}

/**
 * 発注書の履歴から出した仕入先別の発注間隔の提示。
 *
 * 🔴 **自動では入れない**（リードタイムの提示と扱いが違う）＝押した時点で
 * 初めて補充日数へコピーされる。理由＝この値は発注の【時期】ではなく【数量】を
 * 増やす＝そのぶん現金が在庫に変わる。どこまで積むかは資金繰りの判断で、
 * 我々には決められない。
 *
 * 提示が無いとき（実績不足・埋める商品が無い・プラン外の403）は何も描画しない。
 */
export function ReplenishSuggestionCard({ onApplied, disabled }: Props) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<ReplenishApplyResult | null>(null);

  const suggestionsQuery = useQuery({
    queryKey: REPLENISH_SUGGESTION_QUERY_KEY,
    queryFn: listReplenishSuggestions,
    retry: false,
  });

  const apply = useMutation({
    mutationFn: applyReplenishSuggestion,
    onSuccess: (applied) => {
      setResult(applied);
      queryClient.invalidateQueries({
        queryKey: REPLENISH_SUGGESTION_QUERY_KEY,
      });
      onApplied();
    },
  });

  const suggestions = suggestionsQuery.data ?? [];

  if (result) {
    return (
      <Banner
        tone="success"
        title={t("発注間隔から補充日数を設定しました")}
        onDismiss={() => setResult(null)}
      >
        <p>{applySummary(result)}</p>
      </Banner>
    );
  }

  if (suggestionsQuery.isLoading || suggestionsQuery.isError) return null;
  if (suggestions.length === 0) return null;

  return (
    <Card>
      <BlockStack gap="300">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            {t("発注の間隔から補充日数を提案")}
          </Text>
          <Text as="p" tone="subdued">
            {t(
              "実際にその仕入先へ発注している間隔です。採用すると1回の発注でその日数ぶんまで補充するようになります（発注のタイミングは変わらず、数量だけ増えます）。",
            )}
          </Text>
        </BlockStack>

        {suggestions.map((suggestion) => (
          <InlineStack
            key={suggestion.supplier_id}
            align="space-between"
            blockAlign="center"
            gap="300"
            wrap
          >
            <BlockStack gap="050">
              <Text as="span" fontWeight="semibold">
                {suggestion.supplier_name ?? t("名称未設定の仕入先")}
              </Text>
              <Text as="span" tone="subdued">
                {t(
                  "いつもの間隔 {days}日（発注{count}回ぶん）／未設定の商品 {fillable}件",
                )
                  .replace("{days}", String(suggestion.interval_days))
                  .replace("{count}", String(suggestion.sample_size + 1))
                  .replace(
                    "{fillable}",
                    String(suggestion.fillable_variant_count),
                  )}
              </Text>
            </BlockStack>
            <Button
              onClick={() => apply.mutate(suggestion.supplier_id)}
              loading={
                apply.isPending && apply.variables === suggestion.supplier_id
              }
              disabled={disabled || apply.isPending}
            >
              {t("採用する")}
            </Button>
          </InlineStack>
        ))}

        {apply.isError && (
          <Banner tone="critical" title={t("採用に失敗しました")}>
            <p>{(apply.error as Error).message}</p>
          </Banner>
        )}
      </BlockStack>
    </Card>
  );
}
